import React, { useState, useRef, useEffect, useCallback } from "react";
import crypto from "crypto-js";
import { api } from "../../config/api";
import { util } from "../../config/util";
import { Cookies } from "../../constants/Cookies";
import { pages } from "../../constants/PagePaths";
import styles from "./verificationPage.module.scss";
import VerificationReport from "./VerificationReport";
import { SAMPLE_VERIFICATION_RESULT } from "./sampleData";

type Step = "dashboard" | "upload" | "loading" | "report";
type AuthState = "checking" | "not_logged_in" | "no_permission" | "pending_approval" | "rejected" | "authorized";

const VERDICT_LABELS: Record<string, string> = {
  good: "적합",
  caution: "조건부",
  warning: "확인필요",
};

const VERDICT_COLORS: Record<string, string> = {
  good: "#059669",
  caution: "#d97706",
  warning: "#dc2626",
};

const VerificationPage: React.FC = () => {
  const [step, setStep] = useState<Step>("upload");
  const [results, setResults] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [authState, setAuthState] = useState<AuthState>("checking");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showApplyForm, setShowApplyForm] = useState(false);
  const [applySubmitting, setApplySubmitting] = useState(false);
  // applyDone 제거됨 - pending_approval 상태로 대체
  const [applyForm, setApplyForm] = useState({
    name: "",
    phone: "",
    email: "",
    organization: "",
    department: "",
    content: "",
  });

  const [showSample, setShowSample] = useState(false);

  // Dashboard state
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [historyList, setHistoryList] = useState<any[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [isFromHistory, setIsFromHistory] = useState(false);

  const PAGE_SIZE = 10;

  const [rejectReason, setRejectReason] = useState("");

  useEffect(() => {
    const checkAuth = async () => {
      const token = util.getCookie(Cookies.authKey);
      if (!token) {
        setAuthState("not_logged_in");
        return;
      }
      // 로그인만 되어 있으면 바로 사용 가능
      setAuthState("authorized");
      setStep("dashboard");
    };
    checkAuth();
  }, []);

  const loadDashboard = useCallback(async () => {
    setDashboardLoading(true);
    try {
      const res = await api.getVerificationDashboard();
      if (res.data.code === 1) {
        setDashboardData(res.data.result);
      }
    } catch {
      // silent fail
    } finally {
      setDashboardLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async (page: number = 1) => {
    setHistoryLoading(true);
    try {
      const res = await api.getVerificationHistory(page, PAGE_SIZE);
      if (res.data.code === 1) {
        const { items, total, total_pages } = res.data.result;
        setHistoryList(items);
        setHistoryTotal(total);
        setHistoryPage(page);
        setHistoryTotalPages(total_pages);
      }
    } catch {
      // silent fail
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  // Load dashboard and history when entering dashboard step
  useEffect(() => {
    if (step === "dashboard" && authState === "authorized") {
      loadDashboard();
      loadHistory(1);
    }
  }, [step, authState, loadDashboard, loadHistory]);

  const handleHistoryClick = async (id: number) => {
    setHistoryLoading(true);
    try {
      const res = await api.getVerificationHistoryDetail(id);
      if (res.data.code === 1) {
        const detail = res.data.result;
        setResults([detail.full_result]);
        setProjects([]);
        setIsFromHistory(true);
        setStep("report");
      }
    } catch {
      alert("이력을 불러오는 중 오류가 발생했습니다.");
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleHistoryDelete = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    if (!window.confirm("이 검증 이력을 삭제하시겠습니까?")) return;
    setDeletingId(id);
    try {
      await api.deleteVerificationHistory(id);
      loadHistory(historyPage);
      loadDashboard();
    } catch {
      alert("삭제 중 오류가 발생했습니다.");
    } finally {
      setDeletingId(null);
    }
  };

  const processFile = async (file: File) => {
    const fileReader = new FileReader();
    fileReader.onloadend = async () => {
      try {
        const str = fileReader.result as string;
        const key = str.substring(0, 5);
        const value = str.substring(5);
        const decrypted = JSON.parse(
          crypto.AES.decrypt(value, key).toString(crypto.enc.Utf8)
        );

        const normalProjects = Array.isArray(decrypted)
          ? decrypted.filter(
              (p: any) =>
                p.type === "normal" || p.project_type === "normal"
            )
          : [];

        if (normalProjects.length === 0) {
          alert("검증 가능한 설계내역 프로젝트가 없습니다.");
          return;
        }

        setStep("loading");
        setProjects(normalProjects);
        setIsFromHistory(false);

        try {
          const response = await api.verifyProjects(normalProjects);
          if (response.data.code === 1) {
            setResults(response.data.result);
            setStep("report");
          } else {
            alert(response.data.message || "검증 중 오류가 발생했습니다.");
            setStep("upload");
          }
        } catch (error: any) {
          if (error.response?.status === 403) {
            setAuthState("no_permission");
            setStep("upload");
            return;
          }
          alert(
            `검증 중 오류가 발생했습니다: ${error.response?.data?.message || error.message || "다시 시도해 주세요."}`
          );
          setStep("upload");
        }
      } catch (error) {
        alert("파일을 읽는 중 오류가 발생했습니다. 파일을 다시 확인해 주세요.");
      }
    };

    fileReader.readAsText(file);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      processFile(file);
    }
    event.target.value = "";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && (file.name.endsWith(".ddusul") || file.name.endsWith(".conax"))) {
      processFile(file);
    } else {
      alert(".ddusul 또는 .conax 파일만 업로드 가능합니다.");
    }
  };

  const resetVerification = () => {
    setStep("dashboard");
    setResults([]);
    setProjects([]);
    setIsFromHistory(false);
  };

  const goToUpload = () => {
    setStep("upload");
    setResults([]);
    setProjects([]);
    setIsFromHistory(false);
  };

  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const half = month <= 8 ? "상반기" : "하반기";

  const isGated = authState === "not_logged_in" || authState === "no_permission" || authState === "pending_approval" || authState === "rejected";

  const handleApplySubmit = async () => {
    const { name, phone, email, organization, department, content } = applyForm;
    if (!name.trim() || !phone.trim() || !email.trim() || !organization.trim() || !department.trim() || !content.trim()) {
      alert("모든 항목을 입력해 주세요.");
      return;
    }
    const phoneClean = phone.replace(/-/g, "");
    if (!/^01[016789]\d{7,8}$/.test(phoneClean)) {
      alert("올바른 연락처를 입력해 주세요.");
      return;
    }
    setApplySubmitting(true);
    try {
      const res = await api.postVerificationApplication({
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim(),
        organization: organization.trim(),
        department: department.trim(),
        content: content.trim(),
      });
      if (res.data.code === 1) {
        setAuthState("pending_approval");
      } else {
        alert(res.data.message || "신청 중 오류가 발생했습니다.");
      }
    } catch {
      alert("신청 중 오류가 발생했습니다. 다시 시도해 주세요.");
    } finally {
      setApplySubmitting(false);
    }
  };

  const renderGateBanner = () => {
    if (authState === "pending_approval") {
      return (
        <div className={styles.gateNotice}>
          <div className={styles.gateNoticeIcon} style={{ color: "#d97706" }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
          </div>
          <h3 className={styles.gateNoticeTitle}>승인 대기 중</h3>
          <p className={styles.gateNoticeDesc}>
            이용 신청이 접수되었습니다.<br/>
            관리자 검토 후 승인 결과를 안내드리겠습니다.
          </p>
        </div>
      );
    }
    if (authState === "rejected") {
      return (
        <div className={styles.gateNotice}>
          <div className={styles.gateNoticeIcon} style={{ color: "#dc2626" }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
          </div>
          <h3 className={styles.gateNoticeTitle}>신청이 반려되었습니다</h3>
          {rejectReason && (
            <p className={styles.gateNoticeDesc} style={{ marginBottom: 12 }}>
              사유: {rejectReason}
            </p>
          )}
          <p className={styles.gateNoticeDesc}>다시 신청하시려면 아래 버튼을 눌러주세요.</p>
          <button className={styles.gateNoticeBtn} onClick={() => {
            setShowApplyForm(true);
            setAuthState("no_permission");
          }}>다시 신청하기</button>
        </div>
      );
    }
    if (authState === "not_logged_in") {
      return (
        <div className={styles.gateNotice}>
          <div className={styles.gateNoticeIcon}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
          </div>
          <h3 className={styles.gateNoticeTitle}>승인받지 않은 사용자입니다</h3>
          <p className={styles.gateNoticeDesc}>로그인 후 이용 신청이 가능합니다</p>
          <a href={pages.login} className={styles.gateNoticeBtn}>로그인</a>
        </div>
      );
    }
    if (authState === "no_permission") {
      if (!showApplyForm) {
        return (
          <div className={styles.gateNotice}>
            <div className={styles.gateNoticeIcon}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
            </div>
            <h3 className={styles.gateNoticeTitle}>승인받지 않은 사용자입니다</h3>
            <p className={styles.gateNoticeDesc}>발주기관 담당자만 이용 신청할 수 있습니다</p>
            <button className={styles.gateNoticeBtn} onClick={() => setShowApplyForm(true)}>이용 신청하기</button>
          </div>
        );
      }
      return (
        <div className={styles.applyForm}>
          <h3 className={styles.applyFormTitle}>검증 서비스 이용 신청</h3>
          <div className={styles.applyFormGrid}>
            <label className={styles.applyField}>
              <span>이름 <em>*</em></span>
              <input
                type="text"
                placeholder="이름을 입력해주세요"
                value={applyForm.name}
                onChange={(e) => setApplyForm({ ...applyForm, name: e.target.value })}
              />
            </label>
            <label className={styles.applyField}>
              <span>연락처 <em>*</em></span>
              <input
                type="tel"
                placeholder="010-0000-0000"
                value={applyForm.phone}
                onChange={(e) => setApplyForm({ ...applyForm, phone: e.target.value })}
              />
            </label>
            <label className={styles.applyField}>
              <span>이메일 <em>*</em></span>
              <input
                type="email"
                placeholder="이메일을 입력해주세요"
                value={applyForm.email}
                onChange={(e) => setApplyForm({ ...applyForm, email: e.target.value })}
              />
            </label>
            <label className={styles.applyField}>
              <span>기관명 <em>*</em></span>
              <input
                type="text"
                placeholder="소속 기관명을 입력해주세요"
                value={applyForm.organization}
                onChange={(e) => setApplyForm({ ...applyForm, organization: e.target.value })}
              />
            </label>
            <label className={styles.applyField}>
              <span>부서 <em>*</em></span>
              <input
                type="text"
                placeholder="부서명을 입력해주세요"
                value={applyForm.department}
                onChange={(e) => setApplyForm({ ...applyForm, department: e.target.value })}
              />
            </label>
          </div>
          <label className={styles.applyFieldFull}>
            <span>신청내용 <em>*</em></span>
            <textarea
              placeholder="신청 사유를 입력해주세요"
              rows={4}
              value={applyForm.content}
              onChange={(e) => setApplyForm({ ...applyForm, content: e.target.value })}
            />
          </label>
          <div className={styles.applyFormActions}>
            <button
              className={styles.applyFormCancel}
              onClick={() => setShowApplyForm(false)}
              disabled={applySubmitting}
            >
              취소
            </button>
            <button
              className={styles.applyFormSubmit}
              onClick={handleApplySubmit}
              disabled={applySubmitting}
            >
              {applySubmitting ? "신청 중..." : "신청하기"}
            </button>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderSampleCTA = () => (
    <div className={styles.sampleCTA}>
      <div className={styles.sampleCTAContent}>
        <h3>실제 검증 보고서를 미리 확인해보세요</h3>
        <p>실제 설계내역을 검증한 샘플 보고서를 통해 검증 결과의 품질을 확인할 수 있습니다.</p>
        <button className={styles.sampleCTABtn} onClick={() => setShowSample(true)}>
          샘플 검증 보고서 보기
        </button>
      </div>
    </div>
  );

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const m = d.getMonth() + 1;
    const day = d.getDate();
    return `${m}/${day < 10 ? '0' : ''}${day}`;
  };

  const formatFullDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
  };

  const renderDashboard = () => {
    const d = dashboardData;

    return (
      <div className={styles.dashboardPage}>
        {/* 통계 카드 */}
        <div className={styles.dashStatCards}>
          <div className={styles.dashStatCard}>
            <div className={styles.dashStatIcon}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/>
                <rect x="9" y="3" width="6" height="4" rx="1"/>
              </svg>
            </div>
            <div className={styles.dashStatValue}>{d?.total_runs ?? 0}<span>회</span></div>
            <div className={styles.dashStatLabel}>총 검증 횟수</div>
          </div>

          <div className={styles.dashStatCard}>
            <div className={styles.dashStatIcon}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
            <div className={styles.dashStatValue}>{d?.avg_pass_rate ?? 0}<span>%</span></div>
            <div className={styles.dashStatLabel}>평균 적합률</div>
          </div>

          <div className={styles.dashStatCard}>
            <div className={styles.dashStatIcon}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 20V10"/>
                <path d="M12 20V4"/>
                <path d="M6 20v-6"/>
              </svg>
            </div>
            <div className={styles.dashVerdictDist}>
              {d?.verdict_distribution ? (
                <>
                  <span className={styles.verdictGoodBadge}>
                    적합 {d.verdict_distribution.good}
                  </span>
                  <span className={styles.verdictCautionBadge}>
                    조건부 {d.verdict_distribution.caution}
                  </span>
                  <span className={styles.verdictWarningBadge}>
                    확인 {d.verdict_distribution.warning}
                  </span>
                </>
              ) : (
                <span className={styles.dashStatValue}>-</span>
              )}
            </div>
            <div className={styles.dashStatLabel}>판정 분포</div>
          </div>
        </div>

        {/* 새 검증 시작 버튼 */}
        <div className={styles.dashNewVerification}>
          <button className={styles.dashNewBtn} onClick={goToUpload}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            새 검증 시작
          </button>
        </div>

        {/* 검증 이력 */}
        <div className={styles.dashHistorySection}>
          <h3 className={styles.dashHistoryTitle}>
            검증 이력
            {historyTotal > 0 && (
              <span className={styles.dashHistoryCount}>{historyTotal}건</span>
            )}
          </h3>

          {historyLoading && historyList.length === 0 ? (
            <div className={styles.dashHistoryEmpty}>
              <div className={styles.gateSpinner}>
                <svg viewBox="0 0 50 50" width="32" height="32">
                  <circle cx="25" cy="25" r="20" fill="none" strokeWidth="4" stroke="#e5e7eb"/>
                  <circle cx="25" cy="25" r="20" fill="none" strokeWidth="4" stroke="#1e3a5f" strokeLinecap="round" strokeDasharray="80 120"/>
                </svg>
              </div>
            </div>
          ) : historyList.length === 0 ? (
            <div className={styles.dashHistoryEmpty}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
              </svg>
              <p>아직 검증 이력이 없습니다.</p>
              <p className={styles.dashHistoryEmptySub}>첫 검증을 시작해보세요.</p>
            </div>
          ) : (
            <>
              <div className={styles.dashHistoryTable}>
                <div className={styles.dashHistoryHeader}>
                  <span className={styles.dashColName}>프로젝트명</span>
                  <span className={styles.dashColDate}>검증일</span>
                  <span className={styles.dashColRate}>적합률</span>
                  <span className={styles.dashColVerdict}>판정</span>
                  <span className={styles.dashColAction}></span>
                </div>
                {historyList.map((item) => (
                  <div
                    key={item.id}
                    className={styles.dashHistoryRow}
                    onClick={() => handleHistoryClick(item.id)}
                  >
                    <span className={styles.dashColName}>
                      {item.project_name || "(이름없음)"}
                      {item.project_type && (
                        <span className={styles.dashProjectType}>{item.project_type}</span>
                      )}
                    </span>
                    <span className={styles.dashColDate}>
                      {formatFullDate(item.created_at)}
                    </span>
                    <span className={styles.dashColRate}>
                      {Math.round(item.pass_rate)}%
                    </span>
                    <span className={styles.dashColVerdict}>
                      <span
                        className={styles.dashVerdictBadge}
                        style={{
                          color: VERDICT_COLORS[item.verdict_level] || "#6b7280",
                          background: `${VERDICT_COLORS[item.verdict_level] || "#6b7280"}15`,
                        }}
                      >
                        {VERDICT_LABELS[item.verdict_level] || item.verdict_level}
                      </span>
                    </span>
                    <span className={styles.dashColAction}>
                      <button
                        className={styles.dashDeleteBtn}
                        onClick={(e) => handleHistoryDelete(e, item.id)}
                        disabled={deletingId === item.id}
                        title="삭제"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                        </svg>
                      </button>
                    </span>
                  </div>
                ))}
              </div>

              {/* 페이지네이션 */}
              {historyTotalPages > 1 && (
                <div className={styles.dashPagination}>
                  <button
                    className={styles.dashPageBtn}
                    disabled={historyPage <= 1}
                    onClick={() => loadHistory(historyPage - 1)}
                  >
                    &lt;
                  </button>
                  {Array.from({ length: historyTotalPages }, (_, i) => i + 1)
                    .filter(p => Math.abs(p - historyPage) <= 2 || p === 1 || p === historyTotalPages)
                    .reduce((acc: (number | string)[], p, idx, arr) => {
                      if (idx > 0 && typeof arr[idx - 1] === 'number' && (p as number) - (arr[idx - 1] as number) > 1) {
                        acc.push('...');
                      }
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((p, idx) =>
                      p === '...' ? (
                        <span key={`dots-${idx}`} className={styles.dashPageDots}>...</span>
                      ) : (
                        <button
                          key={p}
                          className={`${styles.dashPageBtn} ${historyPage === p ? styles.dashPageActive : ''}`}
                          onClick={() => loadHistory(p as number)}
                        >
                          {p}
                        </button>
                      )
                    )}
                  <button
                    className={styles.dashPageBtn}
                    disabled={historyPage >= historyTotalPages}
                    onClick={() => loadHistory(historyPage + 1)}
                  >
                    &gt;
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.verificationWrapper}>
      {/* 히어로 헤더 - 리포트/샘플 단계에서는 숨김 */}
      {step !== "report" && !showSample && <div className={styles.heroSection}>
        <h1 className={styles.heroTitle}>설계내역 검증 시스템</h1>
        <p className={styles.heroDesc}>
          제출된 설계내역의 데이터 무결성을 검증하고 보고서를 생성합니다
        </p>
        <div className={styles.heroBaseline}>
          <span className={styles.baselineBadge}>표준품셈 {year}년</span>
          <span className={styles.baselineDivider}>|</span>
          <span className={styles.baselineBadge}>노임단가 {year}년 {half}</span>
          <span className={styles.baselineDivider}>|</span>
          <span className={styles.baselineBadge}>자재단가 {year}년 {month}월</span>
          <span className={styles.baselineDivider}>|</span>
          <span className={styles.baselineBadge}>기계경비 {year}년</span>
        </div>
      </div>}

      {/* 대시보드 (authorized only) */}
      {authState === "authorized" && step === "dashboard" && renderDashboard()}

      {/* 샘플 보고서 (비권한 사용자가 샘플 보기 클릭 시) */}
      {isGated && showSample && (
        <VerificationReport
          results={[SAMPLE_VERIFICATION_RESULT]}
          projects={[]}
          onReset={() => setShowSample(false)}
          isDemo={true}
        />
      )}

      {/* 공통 콘텐츠 영역 (upload 단계 또는 게이트) */}
      {!showSample && (authState === "checking" || (authState === "authorized" && step === "upload") || isGated) && (
        <div className={styles.uploadPage}>
          {/* 대시보드로 돌아가기 (authorized upload일 때) */}
          {authState === "authorized" && step === "upload" && (
            <div className={styles.backToDashboard}>
              <button className={styles.backBtn} onClick={() => setStep("dashboard")}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6"/>
                </svg>
                대시보드로 돌아가기
              </button>
            </div>
          )}

          {/* 프로세스 플로우 - 항상 표시 */}
          <div className={styles.processFlow}>
            <div className={styles.processStep}>
              <div className={styles.processNumber}>1</div>
              <div className={styles.processStepTitle}>파일 업로드</div>
              <div className={styles.processStepDesc}>.ddusul / .conax 파일<br/>드래그 또는 선택</div>
            </div>
            <div className={styles.processArrow}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeDasharray="4 3">
                <path d="M5 12h14M13 6l6 6-6 6"/>
              </svg>
            </div>
            <div className={styles.processStep}>
              <div className={styles.processNumber}>2</div>
              <div className={styles.processStepTitle}>자동 검증</div>
              <div className={styles.processStepDesc}>공인 DB와<br/>항목별 대조 분석</div>
            </div>
            <div className={styles.processArrow}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeDasharray="4 3">
                <path d="M5 12h14M13 6l6 6-6 6"/>
              </svg>
            </div>
            <div className={styles.processStep}>
              <div className={styles.processNumber}>3</div>
              <div className={styles.processStepTitle}>보고서 생성</div>
              <div className={styles.processStepDesc}>PDF 보고서<br/>즉시 다운로드</div>
            </div>
          </div>

          {/* 드롭존 - 권한있을 때만 */}
          {authState === "authorized" && (
            <div
              className={`${styles.dropZone} ${isDragging ? styles.dragging : ""}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <div className={styles.dropIcon}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="12" y1="18" x2="12" y2="12"/>
                  <line x1="9" y1="15" x2="12" y2="12"/>
                  <line x1="15" y1="15" x2="12" y2="12"/>
                </svg>
              </div>
              <div className={styles.dropText}>
                <strong>.ddusul / .conax</strong> 파일을 여기에 드래그하거나
              </div>
              <button className={styles.browseBtn}>파일 선택</button>
              <div className={styles.dropHint}>
                지원 형식: .ddusul, .conax (콘엑스 설계내역 파일)
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".ddusul,.conax"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
            </div>
          )}

          {/* 게이트 배너 - 비권한 사용자일 때 드롭존 자리 */}
          {isGated && renderGateBanner()}

          {/* 로딩 스피너 - checking 상태 */}
          {authState === "checking" && (
            <div className={styles.gateNotice}>
              <div className={styles.gateSpinner}>
                <svg viewBox="0 0 50 50" width="40" height="40">
                  <circle cx="25" cy="25" r="20" fill="none" strokeWidth="4" stroke="#e5e7eb"/>
                  <circle cx="25" cy="25" r="20" fill="none" strokeWidth="4" stroke="#1e3a5f" strokeLinecap="round" strokeDasharray="80 120"/>
                </svg>
              </div>
            </div>
          )}

          {/* 검증 항목 카드 - 항상 표시 */}
          <div className={styles.infoCards}>
            <div className={styles.infoCard}>
              <div className={`${styles.infoIcon} ${styles.iconBlue}`}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/>
                  <rect x="9" y="3" width="6" height="4" rx="1"/>
                  <path d="M9 12l2 2 4-4"/>
                </svg>
              </div>
              <div className={styles.infoContent}>
                <h4>일위대가 검증</h4>
                <p>공종별 세부 비용 구성이 기준 데이터와 일치하는지 확인합니다.</p>
              </div>
            </div>
            <div className={styles.infoCard}>
              <div className={`${styles.infoIcon} ${styles.iconPurple}`}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="4" y1="9" x2="20" y2="9"/>
                  <line x1="4" y1="15" x2="20" y2="15"/>
                  <line x1="10" y1="3" x2="8" y2="21"/>
                  <line x1="16" y1="3" x2="14" y2="21"/>
                </svg>
              </div>
              <div className={styles.infoContent}>
                <h4>단가산출 검증</h4>
                <p>복합 단가 산출에 사용된 계산식이 올바른지 검증합니다.</p>
              </div>
            </div>
            <div className={styles.infoCard}>
              <div className={`${styles.infoIcon} ${styles.iconGreen}`}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/>
                  <line x1="7" y1="7" x2="7.01" y2="7"/>
                </svg>
              </div>
              <div className={styles.infoContent}>
                <h4>자재단가 검증</h4>
                <p>등록된 자재의 단가 정보가 기준 데이터와 일치하는지 확인합니다.</p>
              </div>
            </div>
            <div className={styles.infoCard}>
              <div className={`${styles.infoIcon} ${styles.iconOrange}`}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
                </svg>
              </div>
              <div className={styles.infoContent}>
                <h4>프로젝트 설정 검증</h4>
                <p>요율, 절사, 노임할증 등 원가계산서 설정을 확인합니다.</p>
              </div>
            </div>
          </div>

          {/* 샘플 보고서 CTA - 비권한 사용자에게만 */}
          {isGated && !showSample && renderSampleCTA()}

          {/* 하단 안내 - 항상 표시 */}
          <div className={styles.footerInfo}>
            콘엑스 설계내역 검증 시스템
          </div>
        </div>
      )}

      {/* 로딩 */}
      {authState === "authorized" && step === "loading" && (
        <div className={styles.loadingPage}>
          <div className={styles.loadingContent}>
            <div className={styles.loadingSpinner}>
              <svg viewBox="0 0 50 50">
                <circle cx="25" cy="25" r="20" fill="none" strokeWidth="4" stroke="#e5e7eb"/>
                <circle cx="25" cy="25" r="20" fill="none" strokeWidth="4" stroke="#1e3a5f" strokeLinecap="round" strokeDasharray="80 120"/>
              </svg>
            </div>
            <h2 className={styles.loadingTitle}>검증 진행 중</h2>
            <p className={styles.loadingDesc}>
              설계내역 데이터를 분석하고 있습니다.
              <br />
              잠시만 기다려 주세요.
            </p>
          </div>
        </div>
      )}

      {/* 결과 리포트 */}
      {authState === "authorized" && step === "report" && (
        <VerificationReport
          results={results}
          projects={projects}
          onReset={resetVerification}
          isFromHistory={isFromHistory}
        />
      )}
    </div>
  );
};

export default VerificationPage;
